import { CommonModule } from '@angular/common';
import { Component, effect, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FuseAlertComponent } from '@fuse/components/alert';
import { FuseSplashScreenService } from '@fuse/services/splash-screen';
import { PageError } from 'app/modules/error/error.component';
import { ErrorService } from 'app/modules/error/service/error.service';
import { ActivationStore } from 'app/store/activation.store';

@Component({
    selector: 'app-expired-activation',
    imports: [
        MatIconModule,
        RouterLink,
        MatButtonModule,
        CommonModule,
        FuseAlertComponent,
    ],
    templateUrl: './expired-activation.component.html',
    styleUrl: './expired-activation.component.scss',
})
export class ExpiredActivationComponent {
    activation = {
        isExpired: true,
        message: `You can request a new activation link by clicking 'Request New Link'`,
        newActivatonSent: false,
        showRequestButton: false,
    };
    errorMessage = null;
    hasError = false;
    linkSent = false;
    private _router = inject(Router);
    readonly activationStore = inject(ActivationStore);
    private _route = inject(ActivatedRoute);
    pageError: PageError;

    private _splashService = inject(FuseSplashScreenService);
    private _errorHandler = inject(ErrorService);

    constructor() {
        effect(() => {
            const success = this.activationStore.success();
            const code = this.activationStore.code();
            const errorMsg = this.activationStore.error();

            // 1. Handle standard Success
            if (success) {
                this._router.navigateByUrl('/email-sent');
                return;
            }

            // 2. Handle specific API Codes
            if (code === 'FAILED' || errorMsg === 'Connection error') {
                this.hasError = true;
                this.errorMessage = 'Link generation failed. Please try again.';
                return;
            }

            if (errorMsg) {
                this.hasError = true;
            }
        });
    }

    ngOnInit(): void {
        // Read the query parameter
        const code = this._route.snapshot.queryParamMap.get('code') || '';

        // If the API sent false, we hide the button
        if (code === 'outdated') {
            this.activation.showRequestButton = false;
            this.activation.message =
                'We have already sent a new activation link. Please check your mailbox. You may also contact one of our Customer Service Representatives on 1300 554 884 for assistance.';
            this.activation.newActivatonSent = true;
        } else {
            this.activation.showRequestButton = true;
        }

        this._route.queryParamMap.subscribe((params) => {
            if (code === 'connection_failure') {
                this.hasError = true;
                this.activation.message =
                    params.get('msg') || 'Connection lost. Please try again.';
            }
        });
    }

    requestNewLink(): void {
        const userApiId = this._route.snapshot.queryParamMap.get('uid') || '';
        this.linkSent = true;
        this.hasError = false;

        this.activationStore.requestNewLink({
            userApiId,
            callbacks: {
                onError: (err) => {
                    // Check for HTTP status OR custom API error codes
                    const isRateLimited = err?.error?.code === 'RATE_LIMIT';
                    const isNotFound = err?.code === 'NOT_FOUND';

                    if (isRateLimited) {
                        this.gotoPageError(
                            'Request Exceeded',
                            "To keep things running smoothly for everyone, we've temporarily limited your access. Please wait a moment and try again shortly."
                        );
                    } else {
                        if (isNotFound) {
                            this.errorMessage = err?.message;
                        }
                        this.hasError = true;
                    }
                },
            },
        });
    }

    gotoPageError(title: string = '', customMessage?: string) {
        this._splashService.hide();

        this.pageError = {
            title: title,
            message:
                customMessage ||
                'Something went wrong while loading the page. Please try again.',
            icon: 'mat_outline:arrow_back',
        };

        // Push the error object to your state handler
        this._errorHandler.setError(this.pageError);

        this._router.navigate(['/error'], { replaceUrl: true });
    }
}
